/**
 * The single session.
 *
 * One store holds who is signed in, which company and financial year they are
 * working in, what they are allowed to do, and the reference picklists shared
 * by every entry form. Every module reads from here — there is no second
 * notion of "the current user" anywhere in the application.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { can as canDo, canAccess as canAccessModule, type Action, type PermissionSet } from '@rsc/shared';
import { clearSession, get, patch, post, setScope, setToken, getToken } from '@/lib/api';

export interface SessionUser {
  id: number;
  username: string;
  full_name: string;
  email?: string | null;
  is_superadmin: boolean | number;
  ui_mode?: 'BEGINNER' | 'INTERMEDIATE' | 'ACCOUNTANT';
  theme?: string;
  /** Set when this login is also an employee in HR. */
  employee_ref?: string | null;
}

export interface Company {
  id: number;
  code: string;
  name: string;
  gstin?: string | null;
  state_code?: string | null;
  city?: string | null;
  currency_symbol?: string;
  role_code?: string;
  role_name?: string;
}

export interface FinancialYear {
  id: number;
  company_id?: number;
  name: string;
  start_date: string;
  end_date: string;
  is_closed?: number | boolean;
  is_locked?: number | boolean;
}

/**
 * What `/auth/me` returns.
 *
 * `permissions` is a flat `module -> actions` map. The server has already
 * expanded a superadmin's grants across the whole catalogue.
 */
interface MePayload {
  user: SessionUser;
  companies: Company[];
  company: Company | null;
  financial_year: FinancialYear | null;
  financial_years: FinancialYear[];
  permissions: Record<string, string[]>;
  /** Set when this login is a distributor's or a retailer's: they get the portal, not the ERP. */
  portal?: PortalInfo | null;
}

export interface PortalInfo { kind: 'DISTRIBUTOR' | 'RETAILER'; active: boolean }

interface SwitchPayload {
  token: string;
  company: Company;
  financial_year: FinancialYear;
  financial_years?: FinancialYear[];
  permissions?: Record<string, string[]>;
}

interface SessionState {
  /** False until the first `/auth/me` has settled — prevents a login flash. */
  ready: boolean;
  user: SessionUser | null;
  company: Company | null;
  companies: Company[];
  financialYear: FinancialYear | null;
  /** Short alias kept because the accounting screens read `fy`. */
  fy: FinancialYear | null;
  financialYears: FinancialYear[];
  permissions: PermissionSet;
  portal: PortalInfo | null;
  /** Picklists (units, GST rates, groups, warehouses…) used by entry forms. */
  reference: any;

  bootstrap: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchScope: (companyId: number, fyId?: number | null) => Promise<void>;
  switchTo: (companyId: number, fyId?: number | null) => Promise<void>;
  setUiMode: (mode: NonNullable<SessionUser['ui_mode']>) => Promise<void>;
  loadReference: () => Promise<void>;

  can: (moduleKey: string, action?: Action | string) => boolean;
  canAccess: (moduleKey: string) => boolean;
}

const EMPTY: PermissionSet = { superadmin: false, map: {} };

function toPermissionSet(
  raw: Record<string, string[]> | undefined,
  user: SessionUser | null | undefined,
): PermissionSet {
  return {
    superadmin: !!user?.is_superadmin,
    map: (raw ?? {}) as PermissionSet['map'],
  };
}

export const useSession = create<SessionState>((set, getState) => {
  const apply = (data: MePayload) => {
    const company = data.company ?? data.companies?.[0] ?? null;
    set({
      ready: true,
      user: data.user,
      company,
      companies: data.companies ?? [],
      financialYear: data.financial_year ?? null,
      fy: data.financial_year ?? null,
      financialYears: data.financial_years ?? [],
      permissions: toPermissionSet(data.permissions, data.user),
      portal: data.portal ?? null,
    });
    setScope(company?.id ?? null, data.financial_year?.id ?? null);
  };

  const signedOut = {
    user: null, company: null, companies: [], financialYear: null, fy: null,
    financialYears: [], permissions: EMPTY, reference: null, portal: null,
  };

  return {
    ready: false,
    ...signedOut,

    /**
     * Restore the session on load. A missing or rejected token is not an
     * error — it just means nobody is signed in yet.
     */
    async bootstrap() {
      if (!getToken()) {
        set({ ready: true });
        return;
      }
      try {
        apply(await get<MePayload>('/auth/me'));
        if (getState().company && !getState().portal) await getState().loadReference();
      } catch (err: any) {
        /*
         * Only a refusal ends the session. A connection that dropped, a
         * server restarting or a rate limit are not reasons to throw away a
         * good token and make somebody type their password again.
         */
        const status = Number(err?.status ?? 0);
        if (status === 401 || status === 403) {
          clearSession();
          set({ ready: true, ...signedOut });
        } else {
          set({ ready: true });
        }
      }
    },

    async login(username, password) {
      const result = await post<{ token: string; company?: Company; financial_year?: FinancialYear }>(
        '/auth/login',
        { username, password },
      );
      setToken(result.token);
      setScope(result.company?.id ?? null, result.financial_year?.id ?? null);
      apply(await get<MePayload>('/auth/me'));
      if (getState().company && !getState().portal) await getState().loadReference();
    },

    async logout() {
      try {
        await post('/auth/logout');
      } catch {
        // Signing out locally must work even if the server cannot be reached.
      }
      clearSession();
      set({ ready: true, ...signedOut });
    },

    /**
     * Switch company or financial year. The server issues a fresh token for
     * the new scope, and permissions are company-scoped, so both reload.
     */
    async switchScope(companyId, fyId = null) {
      const res = await post<SwitchPayload>('/auth/switch', { company_id: companyId, fy_id: fyId });
      if (res?.token) setToken(res.token);
      setScope(res?.company?.id ?? companyId, res?.financial_year?.id ?? fyId);
      set({ reference: null });
      apply(await get<MePayload>('/auth/me'));
      await getState().loadReference();
    },

    switchTo(companyId, fyId = null) {
      return getState().switchScope(companyId, fyId);
    },

    async setUiMode(mode) {
      await patch('/auth/preferences', { ui_mode: mode });
      const user = getState().user;
      if (user) set({ user: { ...user, ui_mode: mode } });
    },

    async loadReference() {
      try {
        set({ reference: await get('/masters/reference') });
      } catch {
        // A user without master-data access simply gets no picklists.
        set({ reference: null });
      }
    },

    can(moduleKey, action = 'view') {
      return canDo(getState().permissions, moduleKey, action as Action);
    },

    canAccess(moduleKey) {
      return canAccessModule(getState().permissions, moduleKey);
    },
  };
});

/** Hook form for permission checks inside components. */
/**
 * What this user may do.
 *
 * Memoised against the permission set itself, so `can` and `canAccess` keep
 * the same identity between renders. They are the natural thing to put in a
 * dependency array, and a fresh closure each render turns that into an
 * endless re-render loop.
 */
export function usePermissions() {
  const permissions = useSession((s) => s.permissions);
  return useMemo(() => ({
    permissions,
    can: (moduleKey: string, action: Action | string = 'view') =>
      canDo(permissions, moduleKey, action as Action),
    canAccess: (moduleKey: string) => canAccessModule(permissions, moduleKey),
  }), [permissions]);
}
